import { useMemo, useState } from "react";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { SectionHeader } from "../../components/ui/SectionHeader";
import { useRebalancePreview } from "../../hooks/useRebalancePreview";
import type { PreviewQuery, RebalancePreviewResponse } from "../../lib/api";
import { DecisionBanner } from "./DecisionBanner";
import { type Mode, RebalanceControls } from "./RebalanceControls";
import { RebalanceKpis } from "./RebalanceKpis";
import { RebalanceSkeleton } from "./RebalanceSkeleton";
import { apyByAssetMap, buildTradeRows, buildWeightRows } from "./rows";
import { TradePlan } from "./TradePlan";
import { WeightsTable } from "./WeightsTable";

/**
 * Rebalance section — read-only optimizer preview. Shows the
 * decision, headline KPIs, trade plan (USDC deltas), and target
 * allocation. **Does not** simulate or execute itself — the
 * "Prepare tx" button hands the optimizer's deltas to the cart
 * sidebar, which owns the simulate / execute pipeline.
 */
export function RebalanceSection() {
	const [cashUsdc, setCashUsdc] = useState("");
	const [mode, setMode] = useState<Mode>("regular");
	const [cashBufferWeight, setCashBufferWeight] = useState("0.01");
	const [requiredCashWeight, setRequiredCashWeight] = useState("0.10");

	const query: PreviewQuery = useMemo(() => {
		const q: PreviewQuery = {};
		if (cashUsdc.trim()) q.cashUsdc = cashUsdc.trim();
		if (mode === "deposit") {
			q.mode = "deposit";
			q.cashBufferWeight = cashBufferWeight;
		}
		if (mode === "withdrawal") {
			q.mode = "withdrawal";
			q.requiredCashWeight = requiredCashWeight;
		}
		return q;
	}, [cashUsdc, mode, cashBufferWeight, requiredCashWeight]);

	const { data, error, isFetching } = useRebalancePreview(query);

	return (
		<section className="space-y-4">
			<SectionHeader
				title="Rebalance · path, cost, final allocation"
				subtitle="Read-only optimizer preview — nothing gets signed or broadcast."
				trailing={
					<RebalanceControls
						cashUsdc={cashUsdc}
						mode={mode}
						cashBufferWeight={cashBufferWeight}
						requiredCashWeight={requiredCashWeight}
						resolvedCashRaw={data?.input.cashUsdcRaw}
						onCashUsdcChange={setCashUsdc}
						onModeChange={setMode}
						onCashBufferWeightChange={setCashBufferWeight}
						onRequiredCashWeightChange={setRequiredCashWeight}
					/>
				}
			/>

			{isFetching && !data && <RebalanceSkeleton />}
			{error && <ErrorBanner>{(error as Error).message}</ErrorBanner>}
			{data && <RebalanceBody data={data} />}
		</section>
	);
}

function RebalanceBody({ data }: { data: RebalancePreviewResponse }) {
	const apyByAsset = useMemo(() => apyByAssetMap(data), [data]);
	const trades = useMemo(() => buildTradeRows(data), [data]);
	const weights = useMemo(() => buildWeightRows(data), [data]);

	return (
		<div className="space-y-4">
			<DecisionBanner data={data} />
			<RebalanceKpis data={data} />
			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<TradePlan trades={trades} />
				<WeightsTable rows={weights} apyByAsset={apyByAsset} />
			</div>
		</div>
	);
}
